import { useState } from 'react';
import { api, apiBlob } from '../../../utils/api';
import { toast } from '../../ui/simple-toaster';

/**
 * Hook for finish-day, PDF export, share, and base address actions.
 */
export function useDriverActions({
  driverInfo, loadDriverData,
  setShowFinishModal, setShowExportModal, setShowBaseAddressModal,
}) {
  const [exportLoading, setExportLoading] = useState(false);
  const [isDayOff, setIsDayOff] = useState(() => localStorage.getItem('lawd_is_day_off') === 'true');

  const handleFinishDay = async (drivingTomorrow) => {
    if (!driverInfo) return;
    try {
      await api(`/drivers/${driverInfo.driver_id}/finish-day`, {
        method: 'POST',
        body: JSON.stringify({ driving_tomorrow: drivingTomorrow })
      });

      const newIsDayOff = !drivingTomorrow;
      setIsDayOff(newIsDayOff);
      localStorage.setItem('lawd_is_day_off', newIsDayOff ? 'true' : 'false');

      setShowFinishModal(false);
      loadDriverData();
      toast.success(drivingTomorrow ? 'Day finished. Night rest started' : 'Day finished. Day off started', { duration: 3000 });
    } catch (err) {
      toast.error('Failed to finish day: ' + err.message);
    }
  };

  const handleExportPDF = async () => {
    if (!driverInfo) return;
    setExportLoading(true);
    try {
      const blob = await apiBlob('/export/pdf', {
        method: 'POST',
        body: JSON.stringify({ driver_id: driverInfo.driver_id, days: 14 })
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work_diary_${driverInfo.driver_id.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      setShowExportModal(false);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to export PDF: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSharePDF = async () => {
    if (!driverInfo) return;
    setExportLoading(true);
    try {
      const result = await api('/export/share-link', {
        method: 'POST',
        body: JSON.stringify({ driver_id: driverInfo.driver_id, days: 14 })
      });

      const smsUrl = `sms:?body=${encodeURIComponent(result.sms_text)}`;
      window.location.href = smsUrl;

      setShowExportModal(false);
      toast.success('SMS app opened with share link');
    } catch (err) {
      toast.error('Failed to create share link: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleBaseAddressUpdate = async (baseAddressInput) => {
    if (!driverInfo || !baseAddressInput.trim()) return;
    try {
      const result = await api(`/drivers/${driverInfo.driver_id}/base-address`, {
        method: 'PUT',
        body: JSON.stringify({ base_address: baseAddressInput })
      });
      setShowBaseAddressModal(false);
      toast.success(`Base updated: ${result.base_timezone}`);
      loadDriverData();
    } catch (err) {
      toast.error('Failed to update base address: ' + err.message);
    }
  };

  return {
    isDayOff, exportLoading,
    handleFinishDay, handleExportPDF, handleSharePDF, handleBaseAddressUpdate,
  };
}
